import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiUrl } from '../utils/apiBase';

const AdminUserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [filterRole, setFilterRole] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const formatDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date.toLocaleDateString();
  };

  const describeSubscription = (user) => {
    const subscriptions = Array.isArray(user?.subscriptions) ? user.subscriptions : [];

    if (!subscriptions.length) {
      return {
        badgeLabel: 'No package',
        badgeClass: 'bg-gray-100 text-gray-600',
        detail: 'Never subscribed',
        hasActivePackage: false,
        currentPlanLabel: null,
        billingCycle: null,
      };
    }

    const sortedSubscriptions = [...subscriptions].sort((a, b) => {
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();
      return dateB - dateA;
    });

    const now = Date.now();
    const activeSubscription = sortedSubscriptions.find((subscription) => {
      if (subscription.status !== 'active') return false;
      if (!subscription.expires_at) return true;
      const expiresAt = new Date(subscription.expires_at).getTime();
      return !Number.isNaN(expiresAt) && expiresAt > now;
    });

    if (activeSubscription) {
      const planLabel =
        activeSubscription.subscription_plan?.name ||
        activeSubscription.plan_name ||
        activeSubscription.type ||
        null;

      const billingCycle =
        activeSubscription.subscription_plan?.billing_cycle ||
        activeSubscription.interval ||
        'monthly';

      return {
        badgeLabel: planLabel ? `Active – ${planLabel}` : 'Active package',
        badgeClass: 'bg-green-100 text-green-800',
        detail: formatDate(activeSubscription.expires_at)
          ? `Until ${formatDate(activeSubscription.expires_at)}`
          : planLabel
          ? `Plan: ${planLabel}`
          : 'Auto-renewing',
        hasActivePackage: true,
        currentPlanLabel: planLabel,
        billingCycle: billingCycle,
      };
    }

    const latestSubscription = sortedSubscriptions[0];
    const latestPlanLabel =
      latestSubscription?.subscription_plan?.name ||
      latestSubscription?.plan_name ||
      latestSubscription?.type ||
      null;
    const normalizedStatus = (latestSubscription?.status || 'inactive').replace(/_/g, ' ');

    const latestBillingCycle =
      latestSubscription?.subscription_plan?.billing_cycle ||
      latestSubscription?.interval ||
      'monthly';

    return {
      badgeLabel: `No active package`,
      badgeClass: 'bg-yellow-100 text-yellow-800',
      detail: formatDate(latestSubscription?.created_at)
        ? `Last subscribed ${formatDate(latestSubscription.created_at)} (${normalizedStatus})`
        : `Status: ${normalizedStatus}`,
      hasActivePackage: false,
      currentPlanLabel: latestPlanLabel,
      billingCycle: latestBillingCycle,
    };
  };

  const loadUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl('/admin/users'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load users');
      }

      const data = await response.json();
      setUsers(data.data.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl(`/admin/users/${userId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load user details');
      }

      const data = await response.json();
      setUserDetails(data.data);
    } catch (error) {
      console.error('Error loading user details:', error);
      toast.error('Failed to load user details');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl(`/admin/users/${userId}/status`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          is_active: !currentStatus
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update user status');
      }

      const data = await response.json();
      toast.success(data.message);
      
      // Reload users to reflect the change
      loadUsers();
      
      // Update user details if modal is open
      if (showUserModal && selectedUser?.id === userId) {
        loadUserDetails(userId);
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const cancelUserSubscription = async (userId) => {
    if (!window.confirm('Cancel this user’s subscription (stop renewal) but keep access until period end?')) {
      return;
    }
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl(`/admin/users/${userId}/subscription/cancel`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to cancel subscription');
      }
      toast.success(data.message || 'Subscription cancelled');
      loadUsers();
      if (showUserModal && selectedUser?.id === userId) {
        loadUserDetails(userId);
      }
    } catch (error) {
      console.error('Cancel subscription error:', error);
      toast.error(error?.message || 'Failed to cancel subscription');
    }
  };

  const refundLatestSubscriptionPayment = async (userId) => {
    if (!window.confirm('Refund the latest completed subscription payment for this user? This will also revoke referral rewards for that payment.')) {
      return;
    }
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl(`/admin/users/${userId}/subscription/refund-latest`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        const details = data?.details ? ` (${JSON.stringify(data.details)})` : '';
        throw new Error((data?.message || 'Failed to refund payment') + details);
      }
      toast.success(data.message || 'Payment refunded');
      loadUsers();
      if (showUserModal && selectedUser?.id === userId) {
        loadUserDetails(userId);
      }
    } catch (error) {
      console.error('Refund latest payment error:', error);
      toast.error(error?.message || 'Failed to refund payment');
    }
  };

  const eraseUser = async (userId, userName) => {
    if (!window.confirm(`⚠️ WARNING: This will permanently delete user "${userName}" and ALL their data including properties, subscriptions, and images. This action cannot be undone!\n\nAre you absolutely sure you want to erase this user?`)) {
      return;
    }

    if (!window.confirm(`🚨 FINAL CONFIRMATION: You are about to permanently delete "${userName}" and ALL their data.\n\nThis will:\n- Delete all their properties\n- Delete all property images\n- Delete all subscriptions\n- Delete all contact messages\n- Delete all earnings data\n\nThis action CANNOT be undone!\n\nType "DELETE" to confirm:`)) {
      return;
    }

    const confirmation = window.prompt('Type "DELETE" to confirm user erasure:');
    if (confirmation !== 'DELETE') {
      toast.error('User erasure cancelled');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl(`/admin/users/${userId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        let message = 'Failed to erase user';
        try {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const errData = await response.json();
            message = errData?.message || errData?.error || message;
            if (errData?.details) {
              const details =
                typeof errData.details === 'string'
                  ? errData.details
                  : JSON.stringify(errData.details);
              message = `${message} (${details})`;
            }
          } else {
            const text = await response.text();
            if (text) message = text;
          }
        } catch (e) {
          // ignore
        }
        throw new Error(message);
      }

      const data = await response.json();
      toast.success(data.message);
      
      // Reload users to reflect the change
      loadUsers();
      
      // Close modal if the erased user was selected
      if (showUserModal && selectedUser?.id === userId) {
        setShowUserModal(false);
        setSelectedUser(null);
        setUserDetails(null);
      }
    } catch (error) {
      console.error('Error erasing user:', error);
      toast.error(error?.message || 'Failed to erase user');
    }
  };

  const handleUserClick = async (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
    await loadUserDetails(user.id);
  };

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Filter users based on role and search term
  const filteredUsers = users.filter(user => {
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesRole && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">👥 User Management</h1>
                <p className="text-gray-600">Manage users, view properties, and subscription status</p>
              </div>
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
              >
                ← Back to Dashboard
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Role</label>
                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Users</option>
                  <option value="land_reporter">Land Reporters</option>
                  <option value="land_seeker">Land Seekers</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search Users</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Users ({filteredUsers.length})
              </h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subscribed Package
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subscription
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => {
                    const subscription = describeSubscription(user);
                    return (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.role === 'land_reporter' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {user.role === 'land_reporter' ? 'Land Reporter' : 'Land Seeker'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {user.is_active ? 'Active' : 'Blocked'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            subscription.hasActivePackage
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {subscription.hasActivePackage
                              ? (subscription.currentPlanLabel || 'Subscribed')
                              : 'No package'}
                          </span>
                          {subscription.currentPlanLabel && subscription.hasActivePackage && subscription.billingCycle && (
                            <span className="text-xs text-gray-500 mt-1 capitalize">
                              {subscription.billingCycle}
                            </span>
                          )}
                          {subscription.currentPlanLabel && !subscription.hasActivePackage && (
                            <span className="text-xs text-gray-500 mt-1">
                              {`Last: ${subscription.currentPlanLabel}`}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${subscription.badgeClass}`}>
                            {subscription.badgeLabel}
                          </span>
                          {subscription.detail && (
                            <span className="text-xs text-gray-500 mt-1">
                              {subscription.detail}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleUserClick(user)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            View Details
                          </button>
                          <button
                            onClick={() => toggleUserStatus(user.id, user.is_active)}
                            className={`${
                              user.is_active 
                                ? 'text-red-600 hover:text-red-900' 
                                : 'text-green-600 hover:text-green-900'
                            }`}
                          >
                            {user.is_active ? 'Block' : 'Unblock'}
                          </button>
                          <button
                            onClick={() => eraseUser(user.id, user.name)}
                            className="text-red-800 hover:text-red-900 font-semibold"
                            title="Permanently delete user and all their data"
                          >
                            🗑️ Erase
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* User Details Modal */}
          {showUserModal && selectedUser && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
                <div className="mt-3">
                  {/* Modal Header */}
                  <div className="flex items-center justify-between pb-4 border-b">
                    <h3 className="text-lg font-semibold text-gray-900">
                      User Details: {selectedUser.name}
                    </h3>
                    <button
                      onClick={() => setShowUserModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <span className="sr-only">Close</span>
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Modal Content */}
                  <div className="mt-4 space-y-6">
                    {/* User Info */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Contact Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Name</label>
                          <p className="text-sm text-gray-900">{userDetails?.name || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Email</label>
                          <p className="text-sm text-gray-900">{userDetails?.email || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Phone</label>
                          <p className="text-sm text-gray-900">{userDetails?.phone || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Address</label>
                          <p className="text-sm text-gray-900">{userDetails?.address || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">WhatsApp</label>
                          <p className="text-sm text-gray-900">{userDetails?.whatsapp || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Role</label>
                          <p className="text-sm text-gray-900">
                            {userDetails?.role === 'land_reporter' ? 'Land Reporter' : 'Land Seeker'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Status</label>
                          <p className="text-sm text-gray-900">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              userDetails?.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {userDetails?.is_active ? 'Active' : 'Blocked'}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Land Reporter Properties */}
                    {selectedUser.role === 'land_reporter' && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Properties ({userDetails?.properties?.length || 0})</h4>
                        {userDetails?.properties && userDetails.properties.length > 0 ? (
                          <div className="space-y-3">
                            {userDetails.properties.map((property) => (
                              <div key={property.id} className="bg-white rounded-lg p-3 border">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <h5 className="font-medium text-gray-900">{property.title}</h5>
                                    <p className="text-sm text-gray-600">{property.description}</p>
                                    <p className="text-sm text-gray-500">Price: ${property.price}</p>
                                  </div>
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    property.status === 'approved' 
                                      ? 'bg-green-100 text-green-800'
                                      : property.status === 'pending'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {property.status}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-500">No properties submitted</p>
                        )}
                      </div>
                    )}

                    {/* Land Seeker Subscription Status */}
                    {selectedUser.role === 'land_seeker' && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Subscription Status</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm font-medium text-gray-700">Active Subscription:</span>
                            <span className={`text-sm ${
                              userDetails?.has_active_subscription ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {userDetails?.has_active_subscription ? 'Yes' : 'No'}
                            </span>
                          </div>
                          {userDetails?.has_active_subscription && (
                            <div className="flex justify-between">
                              <span className="text-sm font-medium text-gray-700">Cancelled:</span>
                              <span className={`text-sm ${
                                userDetails?.subscription_cancelled ? 'text-yellow-700' : 'text-green-700'
                              }`}>
                                {userDetails?.subscription_cancelled ? 'Yes (no renewal)' : 'No'}
                              </span>
                            </div>
                          )}
                          {userDetails?.subscription_ends_at && (
                            <div className="flex justify-between">
                              <span className="text-sm font-medium text-gray-700">Usable until:</span>
                              <span className="text-sm text-gray-900">
                                {new Date(userDetails.subscription_ends_at).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                          {typeof userDetails?.subscription_days_left === 'number' && userDetails?.subscription_ends_at && (
                            <div className="flex justify-between">
                              <span className="text-sm font-medium text-gray-700">Days left:</span>
                              <span className="text-sm text-gray-900">
                                {userDetails.subscription_days_left}
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-sm font-medium text-gray-700">Total Subscriptions:</span>
                            <span className="text-sm text-gray-900">{userDetails?.subscriptions?.length || 0}</span>
                          </div>
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => cancelUserSubscription(selectedUser.id)}
                            className="px-3 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-lg"
                          >
                            Cancel Subscription (no renewal)
                          </button>
                          <button
                            type="button"
                            onClick={() => refundLatestSubscriptionPayment(selectedUser.id)}
                            className="px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
                          >
                            Refund Latest Payment
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3 pt-4 border-t">
                      <button
                        onClick={() => setShowUserModal(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        Close
                      </button>
                      <button
                        onClick={() => toggleUserStatus(selectedUser.id, selectedUser.is_active)}
                        className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                          selectedUser.is_active 
                            ? 'bg-red-600 hover:bg-red-700' 
                            : 'bg-green-600 hover:bg-green-700'
                        }`}
                      >
                        {selectedUser.is_active ? 'Block User' : 'Unblock User'}
                      </button>
                      <button
                        onClick={() => {
                          setShowUserModal(false);
                          eraseUser(selectedUser.id, selectedUser.name);
                        }}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-800 hover:bg-red-900 rounded-lg transition-colors"
                        title="Permanently delete user and all their data"
                      >
                        🗑️ Erase User
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUserManagement;

